/**
 * Shared helpers for layer-related behavior.
 */

export const LAYER_LOCK_ATTRIBUTE = "pe-layer-locked";
export const LAYER_PRINT_ATTRIBUTE = "pe-layer-printed";
export const ELEMENT_LOCK_ATTRIBUTE = "pe-element-locked";

export const isLayerLocked = (layerElement: Element | null | undefined): boolean => {
    if (!layerElement) {
        return false;
    }

    return (layerElement.getAttribute(LAYER_LOCK_ATTRIBUTE) ?? "false").toLowerCase() === "true";
}

export const isElementLocked = (element: Element | null | undefined): boolean => {
    if (!element) {
        return false;
    }

    return (element.getAttribute(ELEMENT_LOCK_ATTRIBUTE) ?? "false").toLowerCase() === "true";
}

export const isLayerPrinted = (layerElement: Element | null | undefined): boolean => {
    if (!layerElement) {
        return true;
    }

    return (layerElement.getAttribute(LAYER_PRINT_ATTRIBUTE) ?? "true").toLowerCase() !== "false";
}

export const isElementInLockedLayer = (element: Element | null | undefined): boolean => {
    if (!element) {
        return false;
    }

    const layer = element.closest("g.layer");
    return isLayerLocked(layer);
}

/**
 * Check if an element or its parent layer is locked.
 * Returns 'layer' if parent layer is locked, 'element' if element itself is locked, or false if not locked.
 */
export const getElementLockStatus = (element: Element | null | undefined): 'layer' | 'element' | false => {
    if (!element) {
        return false;
    }

    if (isElementInLockedLayer(element)) {
        return 'layer';
    }

    if (isElementLocked(element)) {
        return 'element';
    }

    return false;
}

/**
 * Check if element is effectively locked (either directly or via parent layer).
 */
export const isElementEffectivelyLocked = (element: Element | null | undefined): boolean => {
    return getElementLockStatus(element) !== false;
}

export const getUnlockedElements = <T extends Element>(elements: (T | null | undefined)[]): T[] => {
    return elements.filter(el => el != null && !isElementInLockedLayer(el)) as T[];
}